import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { api } from '../App';
import { Mail, MailX, ArrowLeft, CheckCircle } from 'lucide-react';

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const [status, setStatus] = useState('loading'); // loading, subscribed, unsubscribed, error, success
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (email) {
      checkStatus();
    } else {
      setStatus('error');
      setMessage('No email address provided');
    }
  }, [email]);

  const checkStatus = async () => {
    try {
      const response = await api.get(`/email/status?email=${encodeURIComponent(email)}`);
      setStatus(response.data.subscribed ? 'subscribed' : 'unsubscribed');
    } catch (error) {
      setStatus('error');
      setMessage('Could not find your account');
    }
  };

  const handleUnsubscribe = async () => {
    try {
      await api.post(`/email/unsubscribe?email=${encodeURIComponent(email)}`);
      setStatus('success');
      setMessage('You have been successfully unsubscribed from Kodo emails.');
    } catch (error) {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  const handleResubscribe = async () => {
    try {
      await api.post(`/email/resubscribe?email=${encodeURIComponent(email)}`);
      setStatus('subscribed');
      setMessage('You have been resubscribed to Kodo emails.');
    } catch (error) {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <p className="text-slate-600">Loading...</p>
            </>
          )}

          {status === 'subscribed' && (
            <>
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-4">Email Preferences</h1>
              <p className="text-slate-600 mb-2">You are currently subscribed to Kodo emails at:</p>
              <p className="font-medium text-slate-900 mb-6">{email}</p>
              <p className="text-slate-600 mb-6">
                We send you updates about your programme progress, weekly reminders, and helpful tips.
              </p>
              <Button
                onClick={handleUnsubscribe}
                variant="outline"
                className="w-full mb-4"
                data-testid="unsubscribe-button"
              >
                <MailX className="w-4 h-4 mr-2" />
                Unsubscribe from emails
              </Button>
              <Link to="/dashboard">
                <Button variant="ghost" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
            </>
          )}

          {status === 'unsubscribed' && (
            <>
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <MailX className="w-8 h-8 text-slate-500" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-4">You're Unsubscribed</h1>
              <p className="text-slate-600 mb-2">You won't receive emails from Kodo at:</p>
              <p className="font-medium text-slate-900 mb-6">{email}</p>
              {message && (
                <p className="text-green-600 mb-6">{message}</p>
              )}
              <p className="text-slate-600 mb-6">
                Changed your mind? You can resubscribe anytime.
              </p>
              <Button
                onClick={handleResubscribe}
                className="w-full mb-4"
                data-testid="resubscribe-button"
              >
                <Mail className="w-4 h-4 mr-2" />
                Resubscribe to emails
              </Button>
              <Link to="/dashboard">
                <Button variant="ghost" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-4">Unsubscribed</h1>
              <p className="text-slate-600 mb-6">{message}</p>
              <p className="text-slate-500 text-sm mb-6">
                You can still access your Kodo account and complete activities — you just won't receive email reminders.
              </p>
              <Link to="/dashboard">
                <Button className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <MailX className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-4">Something Went Wrong</h1>
              <p className="text-slate-600 mb-6">{message}</p>
              <Link to="/">
                <Button className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Go to Homepage
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnsubscribePage;
